import { fetcher } from "../api/fetcher";
import { createHashFile } from "../utils/createHashFile";
import {
  extractFileNameAndExtension,
  formatFileSize,
  previewMimeTypes,
} from "./chatFiles";
import { FrontMessageRequest, FrontMessageResponse } from "../types/messages";
import { ws_instance } from "../websocket/ws-instance";
import { computed, nextTick, reactive, ref } from "vue";
import { MessageItem } from "../types/localMessage";
import { XDate } from "@connect/date-helper";
import { authState } from "./auth";
import { v4 } from "uuid";
import {
  saveScrollPosition,
  scrollIfBottom,
  restoreScrollPosition,
} from "./scrollChatState";
import { waitLoading } from "./loadingMessageImage";
import { readMarkViewState, setlastSendReadMark } from "./readMarkState";
import { getDateOrToday } from "../utils/chat/date";
import { parseTextContent } from "../utils/chat/splitMessage";
import { async_mutex } from "../utils/asyncMutex";
import { router } from "../router";

export const messages = reactive<MessageItem[]>([]);
export const uploadState = reactive({
  percent: 0,
  messageId: "null",
});
export const errorChatState = reactive({
  startMessages: false,
  nextMessages: false,
});
export const loadingChatState = reactive({
  startMessages: true,
});

export async function sendMessage(content = "", file?: File, link?: string) {
  let slice = content;
  if (content.length > 8000) {
    slice = content.slice(0, 8000);
  }
  const normalized = slice.normalize();
  if (file) {
    const local = addLocalMessage(normalized, file, link);
    const m = async_mutex("file_upload");
    await m.lock();
    uploadState.messageId = local;
    try {
      const fileInfo = await uploadFile(file);
      const payload: FrontMessageRequest = {
        type: "File",
        content: {
          ...fileInfo,
          text: normalized,
        },
      };
      const res = (await fetcher.post({
        path: "/chat/message",
        payload: payload,
      })) as FrontMessageResponse;
      setRemoteInfo(local, res);
    } catch (err) {
      if (
        typeof err === "object" &&
        err &&
        "status" in err &&
        err.status === 401
      ) {
        router.replace({ name: "error", params: { errtype: "token-expired" } });
      }
      setErrorSend(local);
    } finally {
      uploadState.percent = 100;
      setTimeout(() => {
        uploadState.percent = 0;
        uploadState.messageId = "null";
        m.unlock();
      }, 100);
    }
  } else {
    const local = addLocalMessage(normalized);
    const payload: FrontMessageRequest = {
      type: "Text",
      content: {
        text: normalized,
      },
    };
    try {
      const res = (await fetcher.post({
        path: "/chat/message",
        payload,
      })) as FrontMessageResponse;
      setRemoteInfo(local, res);
    } catch {
      setErrorSend(local);
    } finally {
    }
  }
}

function progressHandler(event: ProgressEvent) {
  const percent = (event.loaded / event.total) * 40;
  if (percent === 40) {
    uploadState.percent = 90;
  } else {
    uploadState.percent = 10 + percent;
  }
}

async function uploadFile(file: File) {
  return new Promise<{
    fileId: string;
    preview: boolean;
    filename: string;
    size: number;
  }>(async (resolve, reject) => {
    uploadState.percent = 1;
    const hash = await createHashFile(file);
    uploadState.percent = 10;
    const formdata = new FormData();
    formdata.append("file", file);
    formdata.append("name", file.name);
    formdata.append("size", String(file.size));
    formdata.append("md5", hash);

    const ajax = new XMLHttpRequest();
    ajax.upload.addEventListener("progress", progressHandler, false);
    ajax.addEventListener(
      "load",
      () => {
        const status = ajax.status;
        if (status === 200 || status === 201) {
          const res = JSON.parse(ajax.response) as {
            id: string;
            preview: boolean;
          };
          resolve({
            fileId: res.id,
            preview: res.preview,
            filename: file.name,
            size: file.size,
          });
        } else reject(ajax);
      },
      false,
    );
    ajax.addEventListener("error", reject, false);
    ajax.addEventListener("abort", reject, false);
    ajax.open("POST", "/api/file/upload");
    ajax.send(formdata);
  });
}

export function subscribeChatMessages() {
  ws_instance.addEventListener(
    "chat-message",
    (message: FrontMessageResponse) => {
      if (message.author.id === authState.id) return;
      else {
        addMessage(message);
        nextTick(async () => {
          if (message.type === "File" && message?.content.preview) {
            await waitLoading(message.id);
          }
          scrollIfBottom();
        });
      }
    },
  );
  ws_instance.addEventListener("chat-cleared", () => {
    messages.length = 0;
  });
}

function addMessage(message: FrontMessageResponse) {
  if (message.type === "Text") {
    messages.push({
      id: message.id,
      type: message.type,
      status: message.status,
      author: message.author,
      content: {
        text: parseTextContent(message.content.text),
      },
      createAt: XDate.parse(message.createAt).format("HH:mm"),
      createAtDate: new Date(message.createAt),
      updateAt: message.updateAt,
      deleteAt: message.deleteAt,
    });
  } else if (message.type === "File") {
    const { name, extension } = extractFileNameAndExtension(
      message.content.filename,
    );
    messages.push({
      id: message.id,
      type: message.type,
      status: message.status,
      author: message.author,
      content: {
        text: parseTextContent(message.content.text),
        file: {
          filename: name,
          ext: extension,
          fileId: message.content.fileId,
          size: formatFileSize(message.content.size),
          preview: message.content.preview,
        },
      },
      createAt: XDate.parse(message.createAt).format("HH:mm"),
      createAtDate: new Date(message.createAt),
      updateAt: message.updateAt,
      deleteAt: message.deleteAt,
    });
  }
}

function addLocalMessage(content: string, file?: File, link?: string) {
  const id = `local-${v4()}`;
  if (file) {
    const { name, extension } = extractFileNameAndExtension(file.name);
    messages.push({
      local: {
        id,
        sendError: false,
      },
      type: "File",
      status: "Original",
      author: {
        id: authState.id,
        avatar: authState.avatar,
        displayedName: authState.displayedName,
      },
      content: {
        text: parseTextContent(content),
        file: {
          filename: name,
          ext: extension,
          size: formatFileSize(file.size),
          preview: previewMimeTypes.includes(file.type),
          localLink: link,
          originalFile: file,
        },
      },
      createAtDate: new Date(),
      createAt: XDate.parse(new Date()).format("HH:mm"),
    });
  } else {
    messages.push({
      local: {
        id,
        sendError: false,
      },
      type: "Text",
      status: "Original",
      author: {
        id: authState.id,
        avatar: authState.avatar,
        displayedName: authState.displayedName,
      },
      content: {
        text: parseTextContent(content),
      },
      createAtDate: new Date(),
      createAt: XDate.parse(new Date()).format("HH:mm"),
    });
  }
  return id;
}

function setErrorSend(id: string) {
  const msg = messages.find((m) => m.local?.id === id);
  if (msg && msg.local) msg.local.sendError = true;
}

function setRemoteInfo(local: string, remote: FrontMessageResponse) {
  const msg = messages.find((m) => m.local?.id === local);
  if (msg) {
    msg.id = remote.id;
    msg.createAtDate = new Date(remote.createAt);
    msg.createAt = XDate.parse(remote.createAt).format("HH:mm");
  }
}

const sortMessages = () => {
  messages.sort((a, b) => a.createAtDate.getTime() - b.createAtDate.getTime());
};

export interface NewMessagesMark {
  type: "NewMessagesMark";
}

export interface DateMark {
  type: "DateMark";
  text: string;
}

export const messagesList = computed(() => {
  const res: (MessageItem | NewMessagesMark | DateMark)[] = [];
  let last_date_mark: string = "";
  let readmark: { type: "NewMessagesMark" } | undefined = undefined;
  for (const message of messages) {
    const dateMark = getDateOrToday(message.createAtDate);
    if (last_date_mark !== dateMark) {
      last_date_mark = dateMark;
      res.push({
        type: "DateMark",
        text: dateMark,
      });
    }
    if (readmark && message.author.id !== authState.id) {
      res.push(readmark);
      readmark = undefined;
    }
    res.push(message);
    if (readMarkViewState.show && message.id === readMarkViewState.messageId) {
      readmark = {
        type: "NewMessagesMark",
      };
    }
  }
  if (res[res.length - 1]?.type === "NewMessagesMark") res.pop();
  return res;
});

export async function getInitialMessages() {
  loadingChatState.startMessages = true;
  try {
    const res = (await fetcher.get({
      path: "/chat/start-messages",
    })) as {
      data: FrontMessageResponse[];
      readMark: {
        messageId: string;
        at: string;
      };
    };
    if (res.data.length < 20) {
      isFullHisstory.value = true;
    }
    for (const message of res.data) {
      addMessage(message);
    }
    setlastSendReadMark(res.readMark);
    sortMessages();
    errorChatState.startMessages = false;
  } catch {
    errorChatState.startMessages = true;
  } finally {
    loadingChatState.startMessages = false;
  }
}

const isFullHisstory = ref(false);

export async function getNextHistory() {
  if (isFullHisstory.value) return;
  if (messages.length) {
    const res = (await fetcher.get({
      path: `/chat/pag-prev/${messages[0].id}`,
    })) as FrontMessageResponse[];
    saveScrollPosition();
    for (const message of res) {
      addMessage(message);
    }
    sortMessages();
    nextTick(() => {
      restoreScrollPosition();
    });
  } else {
    isFullHisstory.value = true;
  }
}

export async function clearChat() {
  await fetcher.delete({
    path: `/chat`,
  });
}
